import { z } from '@faceup/validation'
import { CustomDocumentType } from '../enums'
import { Language } from '../localization'

const fileStateSchema = z.object({
  name: z.string(),
  folderId: z.string().nullable(),
  language: z.enum(Language).nullable(),
  documentType: z.enum(CustomDocumentType).nullable(),
  fileName: z.string(),
})

const folderStateSchema = z.object({
  name: z.string(),
  parentId: z.string().nullable(),
})

export const CustomDocumentActivityLogEntityType = {
  File: 'File',
  Folder: 'Folder',
} as const
export type CustomDocumentActivityLogEntityType =
  (typeof CustomDocumentActivityLogEntityType)[keyof typeof CustomDocumentActivityLogEntityType]

export const CustomDocumentActivityLogAction = {
  Created: 'Created',
  Updated: 'Updated',
  Deleted: 'Deleted',
} as const
export type CustomDocumentActivityLogAction =
  (typeof CustomDocumentActivityLogAction)[keyof typeof CustomDocumentActivityLogAction]

const withEntityType = <T extends string, U extends z.ZodTypeAny>(entityType: T, actionSchema: U) =>
  z.intersection(z.object({ entityType: z.literal(entityType) }), actionSchema)

const fileActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal(CustomDocumentActivityLogAction.Created),
    before: z.null(),
    after: fileStateSchema,
  }),
  z.object({
    action: z.literal(CustomDocumentActivityLogAction.Updated),
    before: fileStateSchema,
    after: fileStateSchema,
  }),
  z.object({
    action: z.literal(CustomDocumentActivityLogAction.Deleted),
    before: fileStateSchema,
    after: z.null(),
    /**
     * When folder is deleted the whole subtree gets deleted too. This ties all the deletions together.
     */
    batchDeletionId: z.string().optional(),
  }),
])

const folderActionSchema = z.discriminatedUnion('action', [
  z.object({
    action: z.literal(CustomDocumentActivityLogAction.Created),
    before: z.null(),
    after: folderStateSchema,
  }),
  z.object({
    action: z.literal(CustomDocumentActivityLogAction.Updated),
    before: folderStateSchema,
    after: folderStateSchema,
  }),
  z.object({
    action: z.literal(CustomDocumentActivityLogAction.Deleted),
    before: folderStateSchema,
    after: z.null(),
    /**
     * When folder is deleted the whole subtree gets deleted too. This ties all the deletions together.
     */
    batchDeletionId: z.string().optional(),
  }),
])

export const customDocumentActivityLogPayloadSchema = z.union([
  withEntityType(CustomDocumentActivityLogEntityType.File, fileActionSchema),
  withEntityType(CustomDocumentActivityLogEntityType.Folder, folderActionSchema),
])

export type CustomDocumentActivityLogPayload = z.infer<
  typeof customDocumentActivityLogPayloadSchema
>
